import {Component, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AppConfigService} from '@service/config/app-config.service';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
import {ApplicantModel, EmploymentModel, KeyIdentifierModel} from '@service/models';
import {YearMonthFormFieldComponent} from '@shared/form-components/custom-form-fields/year-month/year-month-form-field.component';
import {ContentService} from '../../../constants/content.service';
import {getInstitutionId} from '../../../constants/constant';
import {environment} from '@env/environment';

@Component({
    selector: 'cs-employment-info',
    templateUrl: './employment-info.component.html',
    styleUrls: ['./employment-info.component.scss']
})
export class EmploymentInfoComponent implements OnChanges {
    @ViewChild('yearmonth') yearMonthCmp: YearMonthFormFieldComponent;
    @Input() applicant: ApplicantModel;
    @Input() occupationCollected = false;
    @Input('data-test-id') testId: string = 'employment-info';
    appConfig = AppConfigService.config;
    employeeForm: FormGroup;
    keyIdentifier: KeyIdentifierModel;
    finalSubmitPages: any;
    employmentLengthHelpText = 'Enter the employment duration in years and months.';
    institutionSettings = getInstitutionId(environment.env);
    cssWidthClass = this.institutionSettings.singleColumnLayout ? 'col-sm-12' : 'col-md-6';
    hidePosition = false;
    hideEmployerName = false;
    hidePrimaryJobTitle = false;
    employmentLengthPlaceholder = 'Length of Employment';
    jobTitlePlaceholder = 'Job Title';
    employerNameTitlePlaceholder = 'Employer Name';

    constructor(public contentService: ContentService,
                private fb: FormBuilder,
                private sharedKeyDataService: SharedkeyDataService) {
        this.sharedKeyDataService.keyIdentifier$
            .subscribe((data) => {
                this.keyIdentifier = data;
                if (this.keyIdentifier.loanPurposeConfig) {
                    this.finalSubmitPages = this.keyIdentifier.loanPurposeConfig.finalSubmitPages;
                }
            });

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.applicant && changes.applicant.currentValue) {
            this.applicant = changes.applicant.currentValue;
            this.createForm();
        }
    }

    get employements() {
        return this.employeeForm.controls['employeeAdd'] as FormArray;
    }

    runValidation() {
        this.employements.markAllAsTouched();
        if (this.yearMonthCmp) {
            this.yearMonthCmp.markAsTouched();
        }
    }

    private getLengthValidators() {
        const validators = [];
        if (!this.finalSubmitPages?.hideEmploymentHistoryDuration) {
            validators.push(Validators.required);
        }
        if (this.finalSubmitPages?.employmentHistoryDuration > 0) {
            validators.push(Validators.min(1));
        }
        return validators.length ? validators : null;
    }

    createForm() {
        const employments: EmploymentModel[] = this.applicant.employers || [];
        const employmentAdd = [];
        this.hidePrimaryJobTitle = this.occupationCollected;
        if (employments.length > 0) {
            employments.map((employment, index) => {

                const group = this.fb.group({
                    employmentId: [employment.employmentId],
                    employeeType: [employment.empType, Validators.required],
                    employmentLength: [employment.length, this.getLengthValidators()],
                    employerName: [employment.empName, [Validators.required, Validators.maxLength(40), Validators.pattern('^[A-Za-z0-9 \\-.,&]*$')]],
                    salary: [employment.salary],
                    schedule: [employment.schedule || 'MONTHLY'],
                    position: [employment.position, employment.empType !== 'S' ? [Validators.required, Validators.maxLength(40), Validators.pattern('^[A-Za-z0-9 \\-.,&]*$')] : []],
                    empStatus: [employment.empStatus || 'P']
                });

                employmentAdd.push(group);
                this.hideFieldsBasedOnEmploymentType(employment.empType, group, index);
            });
        } else {
            employmentAdd.push(this.fb.group({
                employmentId: [''],
                employeeType: ['', Validators.required],
                employmentLength: ['', this.getLengthValidators()],
                employerName: ['', [Validators.required, Validators.maxLength(40), Validators.pattern('^[A-Za-z0-9 \\-.,&]*$')]],
                position: ['', [Validators.required, Validators.maxLength(40), Validators.pattern('^[A-Za-z0-9 \\-.,&]*$')]],
                empStatus: ['C']
            }));
        }

        this.employeeForm = this.fb.group({
            employeeAdd: this.fb.array(employmentAdd)
        });

    }

    /**
     * Employer Name is not collected for employment types that hide the field (Retired,
     * Unemployed, Student, Home Maker, Disability, and Self Employed when the business name is
     * left blank), which left empName empty and showed as 'NA' in back office. Fall back to the
     * employment type label, same as the lending flow does.
     */
    getEmployerName(empType: string, employerName: string): string {
        if (employerName) {
            return employerName;
        }
        const empTypeObj = this.appConfig.employmentTypeList?.find(item => item.value === empType);
        return empTypeObj ? empTypeObj.name : employerName;
    }

    private setPositionValidators(group: any, index: number, empType: string) {
        const selfEmployed = empType === 'S';
        const hidden = index === 0 && this.hidePrimaryJobTitle;

        if (selfEmployed) {
            this.hideEmployerName = false;
        }

        group.get('position')?.setValidators(selfEmployed || hidden
            ? []
            : [Validators.required, Validators.pattern('^[A-Za-z0-9 \\-.,&]*$')]);
        group.get('position')?.updateValueAndValidity();
    }

    hideFieldsBasedOnEmploymentType(empType: string, group: any, index: number = 0) {
        this.employerNameTitlePlaceholder = 'Employer Name'
        if (['R', 'N', 'S', 'ST', 'H'].includes(empType)) {
            this.hideEmployerName = true;
            this.hidePosition = false;

            group.get('employerName')?.clearValidators();
            group.get('employerName')?.updateValueAndValidity();

            this.setPositionValidators(group, index, empType);
        } else if (empType === 'D') {

            this.hideEmployerName = true;
            this.hidePosition = true;

            group.get('employerName')?.clearValidators();
            group.get('employerName')?.updateValueAndValidity();

            group.get('position')?.clearValidators();
            group.get('position')?.updateValueAndValidity();

        } else {

            this.hideEmployerName = false;
            this.hidePosition = false;

            group.get('employerName')?.setValidators([Validators.required, Validators.maxLength(40), Validators.pattern('^[A-Za-z0-9 \\-.,&]*$')]);
            group.get('employerName')?.updateValueAndValidity();

            this.setPositionValidators(group, index, empType);
        }

        switch (empType) {
            case 'E':
            case 'F':
            case 'P':
            case 'T':
                this.employmentLengthPlaceholder = 'Years of Employment';
                this.jobTitlePlaceholder = 'Job Title';
                break;
            case 'R':
                this.employmentLengthPlaceholder = 'Years Retired';
                this.jobTitlePlaceholder = 'Income Description';
                break;
            case 'N':
                this.employmentLengthPlaceholder = 'Years Unemployed';
                this.jobTitlePlaceholder = 'Income Description';
                break;
            case 'D':
                this.employmentLengthPlaceholder = 'Time on Disability Income';
                break;
            case 'S':
                this.employmentLengthPlaceholder = 'Years Self Employed';
                this.jobTitlePlaceholder = 'Position';
                this.employerNameTitlePlaceholder = 'Business Name';
                break;
            case 'ST':
                this.employmentLengthPlaceholder = 'Time as a Student';
                this.jobTitlePlaceholder = 'Income Description';
                break;
            case 'O':
                this.employmentLengthPlaceholder = 'Time of this Employment';
                this.jobTitlePlaceholder = 'Job Title';
                break;
            case 'M':
                this.employmentLengthPlaceholder = 'Years in Military';
                this.jobTitlePlaceholder = 'Job Title';
                break;
            case 'FU':
                this.employmentLengthPlaceholder = 'Time Furloughed';
                this.jobTitlePlaceholder = 'Job Title';
                break;
            case 'H':
                this.employmentLengthPlaceholder = 'Years as a Home Maker';
                this.jobTitlePlaceholder = 'Income Description';
                break;
        }
    }

    onEmploymentStatusChange(event, index) {
        const employeeArray = this.employeeForm.get('employeeAdd') as FormArray;
        const group = employeeArray.at(index);
        const empType = event.value;
        this.hideFieldsBasedOnEmploymentType(empType, group, index);
    }

    checkYears(event, index) {
        if (index !== 0 || this.finalSubmitPages.employmentHistoryDuration < 0) {
            return;
        }
        const control = this.employements;
        control.controls[index].get('employmentLength').markAsTouched();

        if (control.controls[index].get('employmentLength').invalid) {
            return;
        }

        const employeeFormValues = this.employeeForm.getRawValue();

        const duration = +employeeFormValues.employeeAdd[index].employmentLength || 0;

        if (duration < this.finalSubmitPages.employmentHistoryDuration) {
            if (control.length < 2) {
                control.push(this.fb.group({
                    employmentId: [''],
                    employeeType: ['', Validators.required],
                    employmentLength: ['', this.getLengthValidators()],
                    employerName: ['', [Validators.required, Validators.maxLength(40), Validators.pattern('^[A-Za-z0-9 \\-.,&]*$')]],
                    salary: [0, Validators.required],
                    schedule: ['MONTHLY'],
                    position: ['', [Validators.required, Validators.maxLength(40), Validators.pattern('^[A-Za-z0-9 \\-.,&]*$')]],
                }));
            }
        } else {
            if (control.length > 1) {
                control.removeAt(control.length - 1);
            }
        }
    }

    removeEmployee(index) {
        if (index !== 0) {
            this.employements.controls[index - 1].get('employmentLength').patchValue(null);
        }
        this.employements.removeAt(index);
    }
}
